import { useState, useEffect } from 'react';
import { getCustomerMediaWithUrls } from '../lib/storage';

const B = {
  green: '#95C11F', greenLight: '#edf7d6', greenDark: '#5a7a0a',
  blue:  '#36A9E1', blueLight:  '#d6effa', blueDark:  '#1a7aab',
};

function Lightbox({ photos, startIdx, onClose }) {
  const [idx, setIdx] = useState(startIdx);
  const p = photos[idx];

  useEffect(() => {
    const h = e => {
      if (e.key === 'Escape')      onClose();
      if (e.key === 'ArrowRight')  setIdx(i => Math.min(i + 1, photos.length - 1));
      if (e.key === 'ArrowLeft')   setIdx(i => Math.max(i - 1, 0));
    };
    window.addEventListener('keydown', h);
    return () => window.removeEventListener('keydown', h);
  }, [photos, onClose]);

  if (!p) return null;

  return (
    <div onClick={onClose} style={{ position:'fixed', inset:0, background:'rgba(0,0,0,0.93)', zIndex:1000, display:'flex', flexDirection:'column', alignItems:'center', justifyContent:'center' }}>
      <div onClick={e => e.stopPropagation()} style={{ position:'relative', display:'flex', flexDirection:'column', alignItems:'center', gap:12, maxWidth:'90vw' }}>
        {p.file_type === 'video'
          ? <video src={p.url} controls style={{ maxWidth:'90vw', maxHeight:'65vh', borderRadius:8 }}/>
          : <img src={p.url} alt={p.file_name} style={{ maxWidth:'90vw', maxHeight:'65vh', borderRadius:8, objectFit:'contain' }}/>
        }
        <div style={{ display:'flex', gap:12, alignItems:'center' }}>
          <button onClick={() => setIdx(i => Math.max(i-1,0))} disabled={idx===0}
            style={{ background:'rgba(255,255,255,0.15)', border:'none', color:'#fff', borderRadius:8, padding:'6px 14px', cursor:idx===0?'default':'pointer', fontSize:18 }}>‹</button>
          <p style={{ margin:0, fontSize:12, color:'rgba(255,255,255,0.6)', fontFamily:'Montserrat,sans-serif', textAlign:'center' }}>
            {idx+1} / {photos.length}<br/>
            <span style={{ fontSize:10, color:'rgba(255,255,255,0.4)' }}>{p.visit_date}</span>
          </p>
          <button onClick={() => setIdx(i => Math.min(i+1,photos.length-1))} disabled={idx===photos.length-1}
            style={{ background:'rgba(255,255,255,0.15)', border:'none', color:'#fff', borderRadius:8, padding:'6px 14px', cursor:idx===photos.length-1?'default':'pointer', fontSize:18 }}>›</button>
        </div>
        <button onClick={onClose} style={{ position:'absolute', top:-40, right:0, background:'none', border:'none', color:'#fff', fontSize:24, cursor:'pointer' }}>✕</button>
      </div>
    </div>
  );
}

export default function CustomerHistory({ customerId, t }) {
  const [media,    setMedia]    = useState([]);
  const [loading,  setLoading]  = useState(true);
  const [error,    setError]    = useState(null);
  const [lightbox, setLightbox] = useState(null);

  useEffect(() => {
    if (!customerId) { setLoading(false); return; }
    getCustomerMediaWithUrls(customerId, 'approved').then(({ media, error }) => {
      if (error) setError('Could not load your photos. Please try again.');
      else setMedia(media);
      setLoading(false);
    });
  }, [customerId]);

  // Group photos by visit date
  const byDate = media.reduce((acc, item) => {
    const date = item.visit_date || 'Unknown date';
    if (!acc[date]) acc[date] = [];
    acc[date].push(item);
    return acc;
  }, {});

  const formatDate = (iso) => {
    try {
      return new Date(iso).toLocaleDateString('en-CA', {
        weekday:'short', year:'numeric', month:'short', day:'numeric'
      });
    } catch { return iso; }
  };

  if (loading) return (
    <div style={{ textAlign:'center', padding:'3rem 1rem' }}>
      <div style={{ width:40, height:40, border:`3px solid ${B.green}`, borderTopColor:'transparent', borderRadius:'50%', margin:'0 auto 14px', animation:'spin 0.8s linear infinite' }}/>
      <style>{`@keyframes spin{to{transform:rotate(360deg)}}`}</style>
      <p style={{ fontSize:13, color:t.textSec, fontFamily:'Montserrat,sans-serif' }}>Loading your photos…</p>
    </div>
  );

  return (
    <div style={{ display:'flex', flexDirection:'column', gap:14 }}>
      {lightbox && <Lightbox photos={lightbox.photos} startIdx={lightbox.idx} onClose={() => setLightbox(null)}/>}

      {/* Header */}
      <div style={{ background:`linear-gradient(135deg, ${B.blue}, ${B.green})`, borderRadius:14, padding:'1rem 1.25rem' }}>
        <p style={{ margin:'0 0 2px', fontSize:11, color:'rgba(255,255,255,0.8)', fontFamily:'Montserrat,sans-serif', fontWeight:600 }}>Property photos on file</p>
        <p style={{ margin:0, fontSize:22, fontWeight:700, color:'#fff', fontFamily:'Montserrat,sans-serif' }}>
          {media.length} file{media.length !== 1 ? 's' : ''}
        </p>
      </div>

      {error && (
        <div style={{ background:'#3a0f0f', borderRadius:10, padding:'10px 14px' }}>
          <p style={{ margin:0, fontSize:12, color:'#f87171', fontFamily:'Montserrat,sans-serif' }}>{error}</p>
        </div>
      )}

      {!error && media.length === 0 && (
        <div style={{ background:t.bgCard, border:`0.5px solid ${t.border}`, borderRadius:14, padding:'2rem', textAlign:'center', boxShadow:t.shadow }}>
          <p style={{ fontSize:32, marginBottom:10 }}>📷</p>
          <p style={{ fontWeight:700, fontSize:14, color:t.text, margin:'0 0 6px', fontFamily:'Montserrat,sans-serif' }}>No photos yet</p>
          <p style={{ margin:0, fontSize:13, color:t.textSec, fontFamily:'Montserrat,sans-serif' }}>
            After each service visit, our team reviews and publishes property photos here for your records.
          </p>
        </div>
      )}

      {Object.entries(byDate)
        .sort(([a], [b]) => new Date(b) - new Date(a))
        .map(([date, items]) => (
          <div key={date} style={{ background:t.bgCard, border:`0.5px solid ${t.border}`, borderRadius:14, padding:'1rem 1.25rem', boxShadow:t.shadow }}>
            <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:10 }}>
              <p style={{ margin:0, fontWeight:700, fontSize:14, color:t.text, fontFamily:'Montserrat,sans-serif' }}>{formatDate(date)}</p>
              <span style={{ background:B.greenLight, color:B.greenDark, fontSize:10, fontWeight:700, padding:'2px 8px', borderRadius:20, fontFamily:'Montserrat,sans-serif' }}>
                {items.length} file{items.length > 1 ? 's' : ''}
              </span>
            </div>
            <div style={{ display:'grid', gridTemplateColumns:'repeat(3,1fr)', gap:6 }}>
              {items.map((item, fi) => (
                <div key={item.id} onClick={() => setLightbox({ photos:items, idx:fi })}
                  style={{ aspectRatio:'1', borderRadius:8, overflow:'hidden', border:`0.5px solid ${t.border}`, cursor:'pointer' }}>
                  {item.file_type === 'video'
                    ? <div style={{ width:'100%', height:'100%', background:'#111', display:'flex', alignItems:'center', justifyContent:'center', color:'#fff', fontSize:22 }}>▶</div>
                    : item.url
                      ? <img src={item.url} alt={item.file_name} style={{ width:'100%', height:'100%', objectFit:'cover' }}/>
                      : <div style={{ width:'100%', height:'100%', background:t.bgSurface }}/>
                  }
                </div>
              ))}
            </div>
            <p style={{ margin:'10px 0 0', fontSize:11, color:t.textHint, fontFamily:'Montserrat,sans-serif' }}>
              Reviewed and published by 247 Snow & Lawn · tap any photo to enlarge
            </p>
          </div>
        ))
      }
    </div>
  );
}
